import React, { useState, useEffect, FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "@/assets/css/style.css";
import { useAppSelector } from "@/store/hooks";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";


const ProductsForm: React.FC = () => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:4000/api/v1";
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { token } = useAppSelector((state) => state.auth);

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [previewUrl, setPreviewUrl] = useState("");

    // product state
    const [name, setName] = useState("");
    const [slug, setSlug] = useState("");
    const [shortDescription, setShortDescription] = useState("");
    const [description, setDescription] = useState("");
    const [featuredImage, setFeaturedImage] = useState<File | null>(null);

    const [inventory, setInventory] = useState({
        stock: 0,
        sku: "",
        gst: 0,
        shippingCharges: 0,
        buyNowLink: "",
    });

    const [seo, setSeo] = useState({
        metaTitle: "",
        metaDescription: "",
        metaKeywords: "",
    });

    const [pricing, setPricing] = useState({
        basePrice: 0,
        discountPrice: 0,
        chargeTax: true,
        inStock: true,
    });

    const [organize, setOrganize] = useState({
        category: "",
        subCategory: "",
        bookType: "",
    });

    const [tags, setTags] = useState<string[]>([]);
    const [rating, setRating] = useState<number>(0);

    // Handle image
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setFeaturedImage(file);
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => setPreviewUrl(reader.result as string);
            reader.readAsDataURL(file);
        } else {
            setPreviewUrl("");
        }
    };

    // Fetch product details if editing
    useEffect(() => {
        if (id) {
            fetch(`${baseUrl}/products/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
                .then((res) => res.json())
                .then((data) => {
                    if (data.status && data.data) {
                        const p = data.data;
                        setName(p.name);
                        setSlug(p.slug);
                        setShortDescription(p.shortDescription);
                        setDescription(p.description);
                        setPreviewUrl(p.featuredImage || "");
                        setInventory(p.inventory);
                        setSeo({
                            metaTitle: p.seo.metaTitle,
                            metaDescription: p.seo.metaDescription,
                            metaKeywords: (p.seo.metaKeywords || []).join(", "),
                        });
                        setPricing(p.pricing);
                        setOrganize(p.organize);
                        setTags(p.tags || []);
                        setRating(p.rating || 0);
                    }
                })
                .catch((err) => console.error("Failed to load product:", err));
        }
    }, [id, baseUrl, token]);

    // Submit handler
    // Submit handler
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const formData = new FormData();

            formData.append("name", name);
            formData.append("slug", slug);
            formData.append("shortDescription", shortDescription);
            formData.append("description", description);

            // inventory
            formData.append("stock", String(inventory.stock));
            formData.append("sku", inventory.sku);
            formData.append("gst", String(inventory.gst));
            formData.append("shippingCharges", String(inventory.shippingCharges));
            formData.append("buyNowLink", inventory.buyNowLink);

            // seo
            formData.append("metaTitle", seo.metaTitle);
            formData.append("metaDescription", seo.metaDescription);
            if (seo.metaKeywords) {
                seo.metaKeywords
                    .split(",")
                    .map((k) => k.trim())
                    .forEach((kw) => formData.append("metaKeywords[]", kw));
            }

            // pricing
            formData.append("basePrice", String(pricing.basePrice));
            formData.append("discountPrice", String(pricing.discountPrice));
            formData.append("chargeTax", String(pricing.chargeTax));
            formData.append("inStock", String(pricing.inStock));

            // organize
            formData.append("category", organize.category);
            if (organize.subCategory) formData.append("subCategory", organize.subCategory);
            if (organize.bookType) formData.append("bookType", organize.bookType);

            // tags
            tags.forEach((tag) => formData.append("tags[]", tag));

            // rating
            formData.append("rating", String(rating));

            // file
            if (featuredImage) {
                formData.append("featuredImage", featuredImage);
            }

            const url = id ? `${baseUrl}/products/${id}` : `${baseUrl}/products`;
            const method = id ? "PUT" : "POST";

            const res = await fetch(url, {
                method,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            const data = await res.json();
            if (data.status) {
                alert(id ? "Product updated!" : "Product added!");
                navigate("/products/list");
            } else {
                alert(data.message || "Something went wrong!");
            }
        } catch (err) {
            console.error("Error saving product:", err);
            alert("Error saving product!");
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="side-app">
            <div className="main-container container-fluid">
                <div className="page-header">
                    <h1 className="page-title">{id ? "Edit Product" : "Add Product"}</h1>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-12 col-lg-8">
                            {/* Product Information */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="card-tile mb-0">Product information</h5>
                                </div>
                                <div className="card-body">
                                    <input
                                        type="text"
                                        className="form-control mb-3"
                                        placeholder="Product name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                    />
                                    <input
                                        type="text"
                                        className="form-control mb-3"
                                        placeholder="Slug"
                                        value={slug}
                                        onChange={(e) => setSlug(e.target.value)}
                                        required
                                    />

                                    <label>Short Description</label>
                                    <CKEditor
                                        editor={ClassicEditor}
                                        data={shortDescription}
                                        onChange={(event, editor) => setShortDescription(editor.getData())}
                                    />

                                    <label>Description</label>
                                    <CKEditor
                                        editor={ClassicEditor}
                                        data={description}
                                        onChange={(event, editor) => setDescription(editor.getData())}
                                    />
                                </div>
                            </div>

                            {/* Media */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">Media</h5>
                                </div>
                                <div className="card-body">
                                    <input type="file" accept="image/*" onChange={handleFileChange} />
                                    {previewUrl && <img src={previewUrl} alt="Preview" className="img-fluid mt-2" />}
                                </div>
                            </div>

                            {/* Inventory */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">Inventory</h5>
                                </div>
                                <div className="card-body">
                                    <label className="form-label">Add to Stock</label>
                                    <input
                                        type="number"
                                        className="form-control mb-2"
                                        placeholder="Stock"
                                        value={inventory.stock}
                                        onChange={(e) => setInventory({ ...inventory, stock: Number(e.target.value) })}
                                    />
                                    <label className="form-label">SKU</label>
                                    <input
                                        type="text"
                                        className="form-control mb-2"
                                        placeholder="SKU"
                                        value={inventory.sku}
                                        onChange={(e) => setInventory({ ...inventory, sku: e.target.value })}
                                    />
                                        <label className="form-label">GST (In %)</label>
                                    <input
                                        type="number"
                                        className="form-control mb-2"
                                        placeholder="GST (%)"
                                        value={inventory.gst}
                                        onChange={(e) => setInventory({ ...inventory, gst: Number(e.target.value) })}
                                    />
                                    <label className="form-label">Shipping Charges</label>
                                    <input
                                        type="number"
                                        className="form-control mb-2"
                                        placeholder="Shipping Charges"
                                        value={inventory.shippingCharges}
                                        onChange={(e) => setInventory({ ...inventory, shippingCharges: Number(e.target.value) })}
                                    />
                                    <label className="form-label">Buy Now Link</label>
                                    <input
                                        type="text"
                                        className="form-control mb-2"
                                        placeholder="Buy Now Link"
                                        value={inventory.buyNowLink}
                                        onChange={(e) => setInventory({ ...inventory, buyNowLink: e.target.value })}
                                    />
                                </div>
                            </div>

                            {/* SEO */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">SEO</h5>
                                </div>
                                <div className="card-body">
                                    <label className="form-label">Meta Title</label>
                                    <input
                                        type="text"
                                        className="form-control mb-2"
                                        placeholder="Meta Title"
                                        value={seo.metaTitle}
                                        onChange={(e) => setSeo({ ...seo, metaTitle: e.target.value })}
                                    />
                                    <label className="form-label">Meta Description</label>
                                    <textarea
                                        className="form-control mb-2"
                                        placeholder="Meta Description"
                                        value={seo.metaDescription}
                                        onChange={(e) => setSeo({ ...seo, metaDescription: e.target.value })}
                                    />
                                    <label className="form-label">Meta Keywords</label>
                                    <input
                                        type="text"
                                        className="form-control mb-2"
                                        placeholder="Meta Keywords (comma separated)"
                                        value={seo.metaKeywords}
                                        onChange={(e) => setSeo({ ...seo, metaKeywords: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-lg-4">
                            {/* Pricing */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">Pricing</h5>
                                </div>
                                <div className="card-body">
                                    <label className="form-label">Base Price</label>
                                    <input
                                        type="number"
                                        className="form-control mb-2"
                                        placeholder="Base Price"
                                        value={pricing.basePrice}
                                        onChange={(e) => setPricing({ ...pricing, basePrice: Number(e.target.value) })}
                                    />
                                    <input
                                        type="number"
                                        className="form-control mb-2"
                                        placeholder="Discount Price"
                                        value={pricing.discountPrice}
                                        onChange={(e) => setPricing({ ...pricing, discountPrice: Number(e.target.value) })}
                                    />
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={pricing.chargeTax}
                                            onChange={(e) => setPricing({ ...pricing, chargeTax: e.target.checked })}
                                        />
                                        <label className="form-check-label">Charge tax on this product</label>
                                    </div>
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={pricing.inStock}
                                            onChange={(e) => setPricing({ ...pricing, inStock: e.target.checked })}
                                        />
                                        <label className="form-check-label">In Stock</label>
                                    </div>
                                </div>
                            </div>

                            {/* Organize */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">Organize</h5>
                                </div>
                                <div className="card-body">
                                    <label className="form-label">Category</label>
                                    <input
                                        type="text"
                                        className="form-control mb-2"
                                        placeholder="Category"
                                        value={organize.category}
                                        onChange={(e) => setOrganize({ ...organize, category: e.target.value })}
                                    />
                                    <label className="form-label">Sub Category</label>
                                    <input
                                        type="text"
                                        className="form-control mb-2"
                                        placeholder="Sub Category"
                                        value={organize.subCategory}
                                        onChange={(e) => setOrganize({ ...organize, subCategory: e.target.value })}
                                    />
                                        <label className="form-label">Book Type</label>
                                    <input
                                        type="text"
                                        className="form-control mb-2"
                                        placeholder="Book Type"
                                        value={organize.bookType}
                                        onChange={(e) => setOrganize({ ...organize, bookType: e.target.value })}
                                    />
                                    <label className="form-label">Tags</label>
                                    <input
                                        type="text"
                                        className="form-control mb-2"
                                        placeholder="Tags (comma separated)"
                                        value={tags.join(", ")}
                                        onChange={(e) => setTags(e.target.value.split(",").map((t) => t.trim()))}
                                    />
                                    <label className="form-label">Rating</label>
                                    <select
                                        className="form-control"
                                        value={rating}
                                        onChange={(e) => setRating(Number(e.target.value))}
                                    >
                                        <option value={0}>Rating</option>
                                        {[1, 2, 3, 4, 5].map((r) => (
                                            <option key={r} value={r}>
                                                {r}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Save */}
                            <div className="card">
                                <div className="card-body">
                                    <button type="submit" className="btn btn-success w-100" disabled={loading}>
                                        {loading ? "Saving..." : id ? "Update Product" : "Publish Product"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProductsForm;

